import React from 'react'
import {
  Webhook,
  Plus,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Edit3,
  Save,
  Send,
  Zap,
  Shield,
  Globe,
  Activity,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  EyeOff,
  Copy,
  FileText,
  Mail,
  Layers,
  Hash,
  FileDown,
} from 'lucide-react'

// ─── Section Wrapper ─────────────────────────────────────────────────────────
function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Page Header & Add Webhook Button
// ─────────────────────────────────────────────────────────────────────────────
function PageHeaderMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Webhooks</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Configure outbound webhooks to send extracted data to your APIs, Azure Functions, or any HTTPS endpoint.</div>
        </div>
        <div className="h-8 px-3 bg-navy-800 rounded-lg flex items-center gap-1.5 cursor-default">
          <Plus className="h-3.5 w-3.5 text-white" />
          <span className="text-[11px] text-white font-medium">Add Webhook</span>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Webhooks List Table
// ─────────────────────────────────────────────────────────────────────────────
function WebhooksTableMockup() {
  const webhooks = [
    { name: 'Nymans CSV Export', url: 'https://nymans-fn.azurewebsites.net/api/webhook', events: ['request.approved'], active: true, secret: true, created: 'May 10, 2026' },
    { name: 'Slack Notification', url: 'https://hooks.slack.com/services/T01/B02/abc', events: ['request.extracted', 'request.approved'], active: true, secret: false, created: 'May 5, 2026' },
    { name: 'Dev Test Endpoint', url: 'https://dev.example.com/api/hook', events: ['request.extracted'], active: false, secret: true, created: 'Apr 28, 2026' },
  ]
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
            <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Webhook</th>
            <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Events</th>
            <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
            <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Security</th>
            <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Created</th>
            <th className="w-12"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {webhooks.map((wh, i) => (
            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-default">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    wh.active ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <Webhook className={`h-3.5 w-3.5 ${wh.active ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{wh.name}</p>
                    <p className="text-[9px] font-mono text-gray-400 truncate max-w-[200px]">{wh.url}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {wh.events.map(evt => (
                    <span key={evt} className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                      <Zap className="h-2.5 w-2.5" />{evt}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3">
                {wh.active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-gray-400">Disabled</span>
                )}
              </td>
              <td className="px-4 py-3">
                {wh.secret ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-violet-600 dark:text-violet-400">
                    <Shield className="h-3 w-3" />Signed
                  </span>
                ) : (
                  <span className="text-[10px] text-gray-400">None</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="text-[10px] text-gray-500 dark:text-gray-400">{wh.created}</span>
              </td>
              <td className="px-4 py-3">
                <div className={`relative inline-flex h-4 w-7 shrink-0 rounded-full transition-colors ${
                  wh.active ? 'bg-[var(--token-success,#3b9b5e)]' : 'bg-[var(--token-error,#d94a44)]'
                }`}>
                  <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
                    wh.active ? 'translate-x-3 ml-0.5' : 'translate-x-0.5'
                  }`} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Create/Edit Webhook Form
// ─────────────────────────────────────────────────────────────────────────────
function WebhookFormMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-5">
      {/* Back link */}
      <div className="flex items-center gap-1.5 text-[10px] text-gray-400 mb-1">
        <ArrowLeft className="h-3 w-3" /> Back to Webhooks
      </div>
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Create Webhook</h2>

      {/* Name + URL row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Name</label>
          <div className="h-8 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[10px] text-gray-400">e.g. Nymans CSV Export</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Endpoint URL</label>
          <div className="h-8 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center">
            <span className="text-[9px] text-gray-400 font-mono">https://your-function.azurewebsites.net/api/webhook</span>
          </div>
          <p className="text-[9px] text-red-500 mt-0.5">URL must use HTTPS</p>
        </div>
      </div>

      {/* Events */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-1.5">Events</label>
        <div className="flex gap-3">
          <div className="px-3 py-2.5 rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-xs cursor-default">
            <div className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300">Request Extracted</span>
            </div>
            <p className="text-[8px] text-blue-500 mt-0.5">Fires when extraction completes (before review)</p>
          </div>
          <div className="px-3 py-2.5 rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-xs cursor-default">
            <div className="flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-[10px] font-medium text-blue-700 dark:text-blue-300">Request Approved</span>
            </div>
            <p className="text-[8px] text-blue-500 mt-0.5">Fires when a request is approved/published</p>
          </div>
        </div>
      </div>

      {/* Signing Secret */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-1">Signing Secret</label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 h-8 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center px-3">
            <span className="text-[9px] text-gray-400 font-mono">••••••••••••••••</span>
            <EyeOff className="h-3 w-3 text-gray-400 absolute right-2.5" />
          </div>
          <div className="h-8 w-8 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center">
            <Copy className="h-3 w-3 text-gray-400" />
          </div>
        </div>
        <p className="text-[8px] text-gray-400 mt-1">
          Used to sign payloads with HMAC-SHA256. Sent in the <code className="text-[8px] bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono">X-Xtract-Signature</code> header.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
        <div className="h-7 px-3 flex items-center border border-gray-200 dark:border-gray-700 rounded-lg">
          <span className="text-[10px] text-gray-600 dark:text-gray-300">Cancel</span>
        </div>
        <div className="h-7 px-3 flex items-center gap-1.5 bg-navy-800 rounded-lg">
          <Save className="h-3 w-3 text-white" />
          <span className="text-[10px] text-white font-medium">Create Webhook</span>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Webhook Detail Panel (header card + stats + actions)
// ─────────────────────────────────────────────────────────────────────────────
function WebhookDetailMockup() {
  return (
    <div className="space-y-4">
      {/* Back link */}
      <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
        <ArrowLeft className="h-3 w-3" /> Back to Webhooks
      </div>

      {/* Header card */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
              <Webhook className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">Nymans CSV Export</span>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                <Globe className="h-3 w-3 flex-shrink-0" />
                <span className="break-all">https://nymans-fn.azurewebsites.net/api/webhook</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                <span className="inline-flex items-center gap-1 text-[9px] font-medium px-2 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                  <Zap className="h-2.5 w-2.5" />request.approved
                </span>
                <span className="inline-flex items-center gap-1 text-[9px] font-medium px-2 py-0.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400">
                  <Shield className="h-2.5 w-2.5" />HMAC Signed
                </span>
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className={`relative inline-flex h-4 w-7 shrink-0 rounded-full bg-[var(--token-success,#3b9b5e)]`}>
              <span className="inline-block h-3 w-3 rounded-full bg-white shadow transform translate-x-3 mt-0.5 ml-0.5" />
            </div>
            <div className="h-7 px-2 flex items-center gap-1 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Send className="h-3 w-3 text-blue-600" />
              <span className="text-[9px] font-medium text-blue-600">Send Test</span>
            </div>
            <div className="h-7 px-2 flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-lg">
              <Edit3 className="h-3 w-3 text-gray-500" />
              <span className="text-[9px] text-gray-600 dark:text-gray-300">Edit</span>
            </div>
            <div className="h-7 px-2 flex items-center gap-1 border border-red-200 dark:border-red-800 rounded-lg">
              <Trash2 className="h-3 w-3 text-red-500" />
              <span className="text-[9px] text-red-600">Delete</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
            <Activity className="h-4 w-4 text-gray-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">24</p>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">Total Deliveries</p>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">21</p>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">Successful</p>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <p className="text-lg font-bold text-red-600 dark:text-red-400">3</p>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">Failed</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Delivery Logs Table
// ─────────────────────────────────────────────────────────────────────────────
function DeliveryLogsMockup() {
  const deliveries = [
    { status: 'success', event: 'request.approved', http: 200, request: 142, time: 'May 21, 2026 14:32', duration: '320ms', csv: true },
    { status: 'failed', event: 'request.approved', http: 500, request: 139, time: 'May 20, 2026 09:15', duration: '1,204ms', csv: false },
    { status: 'success', event: 'request.extracted', http: 200, request: 137, time: 'May 19, 2026 16:48', duration: '185ms', csv: true },
    { status: 'pending', event: 'request.approved', http: null, request: null, time: 'May 21, 2026 14:35', duration: null, csv: false },
  ]

  const statusStyles = {
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  }
  const statusIcons = {
    success: <CheckCircle2 className="h-2.5 w-2.5" />,
    failed: <XCircle className="h-2.5 w-2.5" />,
    pending: <Clock className="h-2.5 w-2.5" />,
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/30">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">Delivery Logs</span>
          <span className="text-[10px] text-gray-400">(24)</span>
        </div>
        <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
      </div>
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_1.2fr_0.5fr_0.8fr_1.5fr_0.7fr_0.7fr_auto] gap-2 px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
        <div>Status</div>
        <div>Event</div>
        <div>HTTP</div>
        <div>Request</div>
        <div>Timestamp</div>
        <div>Duration</div>
        <div>Download</div>
        <div className="w-3"></div>
      </div>
      {/* Rows */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {deliveries.map((d, i) => (
          <div key={i} className="grid grid-cols-[1fr_1.2fr_0.5fr_0.8fr_1.5fr_0.7fr_0.7fr_auto] gap-2 px-4 py-2.5 items-center hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-default">
            <div className="flex items-center gap-1">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium rounded-full ${statusStyles[d.status]}`}>
                {statusIcons[d.status]}
                {d.status}
              </span>
              {d.status === 'failed' && (
                <RefreshCw className="h-3 w-3 text-red-400" />
              )}
            </div>
            <div><span className="text-[10px] font-mono text-gray-600 dark:text-gray-300">{d.event}</span></div>
            <div>
              {d.http ? (
                <span className={`text-[10px] font-mono font-semibold ${d.http >= 200 && d.http < 300 ? 'text-emerald-600' : 'text-red-600'}`}>{d.http}</span>
              ) : (
                <span className="text-[10px] text-gray-400">-</span>
              )}
            </div>
            <div>
              {d.request ? (
                <span className="text-[10px] text-gray-600 dark:text-gray-300 flex items-center gap-0.5">
                  <Hash className="h-2.5 w-2.5 text-gray-400" />{d.request}
                </span>
              ) : (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 font-medium">test</span>
              )}
            </div>
            <div><span className="text-[10px] text-gray-500 dark:text-gray-400">{d.time}</span></div>
            <div><span className="text-[10px] text-gray-400 font-mono">{d.duration || '-'}</span></div>
            <div>
              {d.csv ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-md bg-blue-500 text-white">
                  <FileDown className="h-2.5 w-2.5" />CSV
                </span>
              ) : (
                <span className="text-[10px] text-gray-300 dark:text-gray-600">-</span>
              )}
            </div>
            <div><ChevronRight className="h-3 w-3 text-gray-400" /></div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Expanded Delivery Detail (payload/response)
// ─────────────────────────────────────────────────────────────────────────────
function DeliveryExpandedMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900">
      {/* Delivery row (expanded) */}
      <div className="grid grid-cols-[1fr_1.2fr_0.5fr_0.8fr_1.5fr_0.7fr_0.7fr_auto] gap-2 px-4 py-2.5 items-center bg-gray-50 dark:bg-gray-800/30">
        <div>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CheckCircle2 className="h-2.5 w-2.5" />success
          </span>
        </div>
        <div><span className="text-[10px] font-mono text-gray-600 dark:text-gray-300">request.approved</span></div>
        <div><span className="text-[10px] font-mono font-semibold text-emerald-600">200</span></div>
        <div><span className="text-[10px] text-gray-600 flex items-center gap-0.5"><Hash className="h-2.5 w-2.5 text-gray-400" />142</span></div>
        <div><span className="text-[10px] text-gray-500">May 21, 2026 14:32</span></div>
        <div><span className="text-[10px] text-gray-400 font-mono">320ms</span></div>
        <div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-semibold rounded-md bg-blue-500 text-white">
            <FileDown className="h-2.5 w-2.5" />CSV
          </span>
        </div>
        <div><ChevronDown className="h-3 w-3 text-gray-400" /></div>
      </div>
      {/* Expanded details */}
      <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/20 space-y-3">
        <div>
          <p className="text-[9px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">Request Payload</p>
          <pre className="text-[9px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 rounded-lg p-2.5 font-mono overflow-x-auto">
{`{
  "event": "request.approved",
  "request_id": 142,
  "template_id": 5,
  "fields": { "InvoiceNumber": "INV-2024-042", "Amount": "12,500.00" }
}`}
          </pre>
        </div>
        <div>
          <p className="text-[9px] font-semibold text-gray-400 mb-1 uppercase tracking-wider">Response Body</p>
          <pre className="text-[9px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-900 rounded-lg p-2.5 font-mono overflow-x-auto">
{`{ "status": "ok", "download_url": "https://storage.blob.core.windows.net/exports/142.csv" }`}
          </pre>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Toggle Switch (Active/Disabled states)
// ─────────────────────────────────────────────────────────────────────────────
function ToggleMockup() {
  return (
    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="relative inline-flex h-4 w-7 shrink-0 rounded-full bg-[var(--token-success,#3b9b5e)]">
            <span className="inline-block h-3 w-3 rounded-full bg-white shadow transform translate-x-3 mt-0.5 ml-0.5" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Active</p>
            <p className="text-[9px] text-gray-500">Webhook is receiving events</p>
          </div>
        </div>
        <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
        <div className="flex items-center gap-3">
          <div className="relative inline-flex h-4 w-7 shrink-0 rounded-full bg-[var(--token-error,#d94a44)]">
            <span className="inline-block h-3 w-3 rounded-full bg-white shadow transform translate-x-0 mt-0.5 ml-0.5" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">Disabled</p>
            <p className="text-[9px] text-gray-500">Webhook is paused - no deliveries fired</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Mockup: Delete Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
function DeleteModalMockup() {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden max-w-md">
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delete webhook?</h3>
            <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              This will permanently remove the webhook and its delivery history. This cannot be undone.
            </p>
            <div className="mt-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">Nymans CSV Export</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 font-mono truncate mt-0.5">https://nymans-fn.azurewebsites.net/api/webhook</p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2.5">
        <div className="px-4 py-2 flex items-center border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Keep it</span>
        </div>
        <div className="px-4 py-2 flex items-center gap-1.5 bg-red-600 rounded-lg shadow-sm">
          <span className="text-xs font-semibold text-white">Delete</span>
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// WEBHOOKS GUIDE
// ===========================================================================
export default function WebhooksGuide({ onNavigate }) {
  return (
    <div className="space-y-10">
      {/* Module header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Webhook className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Webhooks</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Configure outbound webhooks that fire when extraction completes or a request is approved. Xtract sends a signed HTTP POST with the full extracted data to your HTTPS endpoint.
          </p>
        </div>
      </div>

      {/* Section 1: Page Header */}
      <Section number={1} title="Page Header & Add Webhook">
        <PageHeaderMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The page title and description explain what webhooks are for. Click the <strong>"Add Webhook"</strong> button (top-right) to open the creation form. The button uses the primary navy brand color.
        </p>
      </Section>

      {/* Section 2: Webhooks List Table */}
      <Section number={2} title="Webhooks List Table">
        <WebhooksTableMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          All configured webhooks appear in a table with 6 columns:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Webhook</strong> - Name + endpoint URL (mono font). Icon color reflects active state (green vs gray).</li>
          <li><strong>Events</strong> - Blue event badges with lightning icon (<code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded">request.extracted</code>, <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded">request.approved</code>).</li>
          <li><strong>Status</strong> - "Active" with green pulse dot or "Disabled" in gray.</li>
          <li><strong>Security</strong> - Shows "Signed" with shield icon if HMAC secret is set, otherwise "None".</li>
          <li><strong>Created</strong> - Date the webhook was created.</li>
          <li><strong>Toggle</strong> - Green/red switch to enable/disable without opening the detail view.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Click any row to navigate to the Webhook Detail panel.
        </p>
      </Section>

      {/* Section 3: Create / Edit Form */}
      <Section number={3} title="Create / Edit Webhook Form">
        <WebhookFormMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The form has 4 fields:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Name</strong> (required) - A human-readable label (max 100 chars).</li>
          <li><strong>Endpoint URL</strong> (required) - Must start with <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded">https://</code>. A red validation message shows if not HTTPS.</li>
          <li><strong>Events</strong> - Toggle one or both event types. At least one must be selected. Active selections have a blue highlighted border.</li>
          <li><strong>Signing Secret</strong> - Optional. Auto-generated if left empty. Masked by default with eye toggle and copy button. Used for HMAC-SHA256 verification via the <code className="text-[11px] bg-gray-100 dark:bg-gray-800 px-1 rounded">X-Xtract-Signature</code> header.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The Submit button is disabled until Name and a valid HTTPS URL are provided.
        </p>
      </Section>

      {/* Section 4: Webhook Detail Panel */}
      <Section number={4} title="Webhook Detail Panel">
        <WebhookDetailMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Click any webhook row to enter the Detail view. The header card shows:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Name + Status badge</strong> - Active (green pulse) or Disabled.</li>
          <li><strong>URL</strong> - Displayed in monospace, breaks across lines if long.</li>
          <li><strong>Event badges</strong> - Blue pills showing subscribed events.</li>
          <li><strong>HMAC Signed badge</strong> - Violet pill if a signing secret is configured.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          Action buttons in the header:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Toggle switch</strong> - Enable/disable the webhook in one click.</li>
          <li><strong>Send Test</strong> - Fires a test payload immediately and records a delivery log.</li>
          <li><strong>Edit</strong> - Opens the form pre-filled with current values.</li>
          <li><strong>Delete</strong> - Opens a confirmation modal (see Section 7).</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          Below the header, 3 stats cards show <strong>Total Deliveries</strong>, <strong>Successful</strong> (emerald), and <strong>Failed</strong> (red) counts.
        </p>
      </Section>

      {/* Section 5: Active/Disabled Toggle */}
      <Section number={5} title="Active / Disabled Toggle">
        <ToggleMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The toggle switch uses <strong>green</strong> for active and <strong>red</strong> for disabled. When disabled, no events are delivered to this webhook - existing deliveries are preserved but new ones are blocked. The toggle is available both in the list table and in the detail header.
        </p>
      </Section>

      {/* Section 6: Delivery Logs */}
      <Section number={6} title="Delivery Logs Table">
        <DeliveryLogsMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          The Delivery Logs section appears below the stats cards in the detail view. It has 8 columns:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Status</strong> - Badge with icon: success (emerald), failed (red), pending (amber). Failed rows show a retry button.</li>
          <li><strong>Event</strong> - The event type that triggered the delivery.</li>
          <li><strong>HTTP</strong> - Response status code (green for 2xx, red otherwise). Shows "-" for pending.</li>
          <li><strong>Request</strong> - Request ID with hash icon, or a "test" badge for test deliveries.</li>
          <li><strong>Timestamp</strong> - When the delivery completed (or was created if pending).</li>
          <li><strong>Duration</strong> - Response time in milliseconds.</li>
          <li><strong>Download</strong> - Blue "CSV" button if the response contains a download URL.</li>
          <li><strong>Expand</strong> - Chevron to expand the row and see payload details.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          The table auto-refreshes every 10 seconds so you can see new deliveries and status changes without reloading. Use the Refresh icon in the header to manually refresh.
        </p>
      </Section>

      {/* Section 7: Expanded Delivery Detail */}
      <Section number={7} title="Expanded Delivery Detail">
        <DeliveryExpandedMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Click any delivery row to expand it. The expanded panel shows up to 3 sections:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Error</strong> (red background) - Only shown for failed deliveries. Displays the error message.</li>
          <li><strong>Request Payload</strong> - The JSON body that was sent to your endpoint. Contains event type, request ID, template ID, and extracted fields.</li>
          <li><strong>Response Body</strong> - The raw response returned by your endpoint. If it contains a URL, the Download button appears in the table row.</li>
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mt-2">
          For failed deliveries, click the <strong>Retry</strong> button (circular arrow icon) next to the status badge to resend. The status flips to "pending" immediately and auto-polls until the result comes back.
        </p>
      </Section>

      {/* Section 8: Delete Confirmation */}
      <Section number={8} title="Delete Confirmation Modal">
        <DeleteModalMockup />
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Clicking Delete shows a confirmation modal with the webhook name and URL. Two scenarios:
        </p>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 ml-4 list-disc">
          <li><strong>Standard delete</strong> - "Delete webhook?" prompt. Click "Delete" to confirm or "Keep it" to cancel.</li>
          <li><strong>Linked to templates</strong> - If the webhook is linked to one or more templates, the backend returns a 409. The modal updates to warn that deleting will stop deliveries for those templates, and the button changes to "Delete anyway" (force delete).</li>
        </ul>
      </Section>

      {/* Section 9: Empty State / First Run */}
      <Section number={9} title="Empty State (First Run)">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          If no webhooks have been created yet, you'll see an onboarding panel instead of the list:
        </p>
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
            <Webhook className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">No webhooks yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">Create your first webhook to automatically export extracted data when requests are approved.</p>
          <button className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-navy-800 text-white text-sm font-medium">
            <Plus className="h-4 w-4" />
            Create Your First Webhook
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This is the starting point for teams setting up automated data export. Click the button to go directly to the creation form.
        </p>
      </Section>

      {/* Tips */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-xs font-semibold text-blue-900 dark:text-blue-200">HMAC Verification</span>
            </div>
            <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">
              Always set a signing secret and verify the <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">X-Xtract-Signature</code> header in your endpoint to ensure requests come from Xtract.
            </p>
          </div>
          <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Globe className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-900 dark:text-purple-200">HTTPS Required</span>
            </div>
            <p className="text-[11px] text-purple-700 dark:text-purple-300 leading-relaxed">
              All webhook URLs must use HTTPS. This ensures payload data is encrypted in transit. HTTP endpoints will be rejected during creation.
            </p>
          </div>
          <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <RefreshCw className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs font-semibold text-green-900 dark:text-green-200">Retry Failed Deliveries</span>
            </div>
            <p className="text-[11px] text-green-700 dark:text-green-300 leading-relaxed">
              Failed deliveries can be retried individually from the delivery logs. The retry re-sends the original payload and polls for a result automatically.
            </p>
          </div>
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Send className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">Test Before Linking</span>
            </div>
            <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
              Use the "Send Test" button to validate your endpoint returns 2xx before linking the webhook to a template. Test deliveries show as "test" badge in the logs.
            </p>
          </div>
        </div>
      </section>

      {/* Related Modules */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Related Modules</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'templates', label: 'Templates', icon: Layers },
            { id: 'requests', label: 'Requests', icon: FileText },
            { id: 'email-queue', label: 'Email Queue', icon: Mail },
          ].map((mod) => {
            const Icon = mod.icon
            return (
              <button key={mod.label} onClick={() => onNavigate(mod.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <Icon className="h-3.5 w-3.5" />
                {mod.label}
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}

